import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Select,
  Input,
  Button,
  Space,
  Tag,
  DatePicker,
  Row,
  Col,
  message,
  Modal,
  Typography
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
  EyeOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  BugOutlined
} from '@ant-design/icons';
import { adminService, SystemLog, LogFilters } from '../../../services/adminService';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;
const { Text, Paragraph } = Typography;

const SystemLogs: React.FC = () => {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [filters, setFilters] = useState<LogFilters>({});
  const [selectedLog, setSelectedLog] = useState<SystemLog | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);

  useEffect(() => {
    loadLogs();
  }, [pagination.current, pagination.pageSize, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await adminService.getSystemLogs({
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
      });

      if (response.success && response.data) {
        setLogs(response.data.logs);
        setPagination(prev => ({
          ...prev,
          total: response.data?.pagination?.total || 0,
        }));
      }
    } catch (error) {
      console.error('載入系統日誌失敗:', error);
      message.error('載入系統日誌失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
      total: pagination.total,
    });
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates.length === 2) {
      setFilters(prev => ({
        ...prev,
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD'),
      }));
    } else {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters.startDate;
        delete newFilters.endDate;
        return newFilters;
      });
    }
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const showLogDetail = (log: SystemLog) => {
    setSelectedLog(log);
    setDetailModalVisible(true);
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'info':
        return <InfoCircleOutlined style={{ color: '#1890ff' }} />;
      case 'warn':
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'debug':
        return <BugOutlined style={{ color: '#722ed1' }} />;
      default:
        return <FileTextOutlined />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'info': return 'blue';
      case 'warn': return 'orange';
      case 'error': return 'red';
      case 'debug': return 'purple';
      default: return 'default';
    }
  };

  const getLevelText = (level: string) => {
    switch (level) {
      case 'info': return '信息';
      case 'warn': return '警告';
      case 'error': return '錯誤';
      case 'debug': return '調試';
      default: return level;
    }
  };

  const exportLogs = async () => {
    try {
      message.info('日誌導出功能開發中...');
      // TODO: 實現日誌導出功能
    } catch (error) {
      message.error('導出失敗');
    }
  };

  const columns: ColumnsType<SystemLog> = [
    {
      title: '時間',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
      render: (timestamp) => (
        <Text code>
          {dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss')}
        </Text>
      ),
      sorter: true,
    },
    {
      title: '級別',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (level) => (
        <Tag color={getLevelColor(level)} icon={getLevelIcon(level)}>
          {getLevelText(level)}
        </Tag>
      ),
    },
    {
      title: '來源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source) => (
        <Tag color="geekblue">{source}</Tag>
      ),
    },
    {
      title: '消息',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (message) => (
        <Text ellipsis={{ tooltip: message }}>
          {message}
        </Text>
      ),
    },
    {
      title: '用戶ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 120,
      render: (userId) => userId ? <Text code>{userId}</Text> : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => showLogDetail(record)}
          title="查看詳情"
        />
      ),
    },
  ];

  return (
    <div className="system-logs">
      {/* 篩選和操作區域 */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={6} lg={4}>
            <Search
              placeholder="搜索日誌消息"
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="日誌級別"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('level', value)}
            >
              <Option value="info">信息</Option>
              <Option value="warn">警告</Option>
              <Option value="error">錯誤</Option>
              <Option value="debug">調試</Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={4} lg={3}>
            <Select
              placeholder="來源"
              allowClear
              style={{ width: '100%' }}
              onChange={(value) => handleFilterChange('source', value)}
            >
              <Option value="auth">認證</Option>
              <Option value="parking">停車</Option>
              <Option value="payment">支付</Option>
              <Option value="system">系統</Option>
              <Option value="api">API</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={6}>
            <RangePicker
              style={{ width: '100%' }}
              onChange={handleDateRangeChange}
              placeholder={['開始日期', '結束日期']}
            />
          </Col>
          <Col xs={24} sm={12} md={4} lg={4}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadLogs}
                loading={loading}
              >
                刷新
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={exportLogs}
              >
                導出
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 日誌表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 項，共 ${total} 項`,
            pageSizeOptions: ['20', '50', '100', '200'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="small"
        />
      </Card>

      {/* 日誌詳情模態框 */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            日誌詳情
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            關閉
          </Button>
        ]}
        width={800}
      >
        {selectedLog && (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text strong>時間：</Text>
                <Text code>
                  {dayjs(selectedLog.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                </Text>
              </Col>
              <Col span={12}>
                <Text strong>級別：</Text>
                <Tag color={getLevelColor(selectedLog.level)} icon={getLevelIcon(selectedLog.level)}>
                  {getLevelText(selectedLog.level)}
                </Tag>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <Text strong>來源：</Text>
                <Tag color="geekblue">{selectedLog.source}</Tag>
              </Col>
              <Col span={12}>
                <Text strong>用戶ID：</Text>
                <Text code>{selectedLog.userId || '無'}</Text>
              </Col>
            </Row>

            <div style={{ marginBottom: 16 }}>
              <Text strong>消息：</Text>
              <Paragraph
                copyable
                style={{
                  background: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  marginTop: 8,
                }}
              >
                {selectedLog.message}
              </Paragraph>
            </div>

            {selectedLog.metadata && (
              <div>
                <Text strong>元數據：</Text>
                <Paragraph
                  copyable
                  style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    marginTop: 8,
                    fontFamily: 'monospace',
                  }}
                >
                  <pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                </Paragraph>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SystemLogs;